import { memo, useMemo } from 'react';
import dayjs from 'dayjs';
import StatusPill from '../StatusPill';
import { P } from '../../theme/tokens';
import { Agenda, AgendaDay, Chip, Empty, EmptyCell, Root, RowHead, RowLabel, Scroll, Stack, Table, Td, Th } from './styled';

const DAY_COL_MIN = 96;
const ROW_COL = 140;

const EventChip = memo(function EventChip({ event, color, rowLabel, meta, onSelect }) {
  const label = [event.time, event.title, event.detail, rowLabel, event.statusLabel].filter(Boolean).join(', ');
  return (
    <Chip
      type="button"
      $color={color}
      $muted={event.muted}
      $struck={event.struck}
      onClick={() => onSelect?.(event.id)}
      aria-label={label}
      title={label}
    >
      <b>{event.time}</b>
      <span>{event.title}</span>
      {meta && <small>{meta}</small>}
    </Chip>
  );
});

function useBuckets(events) {
  return useMemo(() => {
    const byCell = new Map();
    const byDay = new Map();
    const push = (map, key, e) => {
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    };
    events.forEach((e) => {
      push(byCell, `${e.rowId}|${e.date}`, e);
      push(byDay, e.date, e);
    });
    return { byCell, byDay };
  }, [events]);
}

function WeekTable({ days, rows, today, rowHeader, emptyText, byCell, onEventClick }) {
  return (
    <Scroll>
      <Table $minWidth={ROW_COL + days.length * DAY_COL_MIN}>
        <thead>
          <tr>
            <Th scope="col" $width={ROW_COL}>
              {rowHeader}
            </Th>
            {days.map((date) => (
              <Th key={date} scope="col" $today={date === today} aria-current={date === today ? 'date' : undefined}>
                {dayjs(date).format('ddd')}
                <small>{dayjs(date).format('MMM D')}</small>
              </Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length ? (
            rows.map((row) => (
              <tr key={row.id}>
                <RowHead scope="row" title={row.title || row.label}>
                  <RowLabel $color={row.color}>
                    <span>{row.label}</span>
                  </RowLabel>
                </RowHead>
                {days.map((date) => (
                  <Td key={date} $today={date === today}>
                    <Stack>
                      {(byCell.get(`${row.id}|${date}`) || []).map((e) => (
                        <EventChip key={e.id} event={e} color={row.color} rowLabel={row.title || row.label} onSelect={onEventClick} />
                      ))}
                    </Stack>
                  </Td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <EmptyCell colSpan={days.length + 1}>{emptyText}</EmptyCell>
            </tr>
          )}
        </tbody>
      </Table>
    </Scroll>
  );
}

function WeekAgenda({ days, today, emptyText, byDay, rowById, onEventClick }) {
  const activeDays = days.filter((date) => byDay.has(date));
  return (
    <Agenda>
      {!activeDays.length && <Empty>{emptyText}</Empty>}
      {activeDays.map((date) => (
        <AgendaDay key={date} $today={date === today} aria-label={dayjs(date).format('dddd, MMMM D')}>
          <h3>
            {dayjs(date).format('ddd, MMM D')}
            {date === today && <StatusPill color={P.navy}>Today</StatusPill>}
          </h3>
          <Stack>
            {byDay.get(date).map((e) => {
              const row = rowById.get(e.rowId);
              return (
                <EventChip
                  key={e.id}
                  event={e}
                  meta={[row?.label, e.detail, e.muted && e.statusLabel].filter(Boolean).join(' · ')}
                  color={row?.color || P.subtle}
                  rowLabel={row?.title || row?.label}
                  onSelect={onEventClick}
                />
              );
            })}
          </Stack>
        </AgendaDay>
      ))}
    </Agenda>
  );
}

function WeekCalendar({ days, rows, events, today, rowHeader = 'Row', emptyText = 'Nothing scheduled.', stale, onEventClick }) {
  const { byCell, byDay } = useBuckets(events);
  const rowById = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);

  return (
    <Root $stale={stale}>
      <WeekTable
        days={days}
        rows={rows}
        today={today}
        rowHeader={rowHeader}
        emptyText={emptyText}
        byCell={byCell}
        onEventClick={onEventClick}
      />
      <WeekAgenda days={days} today={today} emptyText={emptyText} byDay={byDay} rowById={rowById} onEventClick={onEventClick} />
    </Root>
  );
}

export default memo(WeekCalendar);
